import React from "react";
import {
  Box, Typography, FormControlLabel,
  Select, MenuItem, TextField, Collapse,
} from "@mui/material";
import { DAYS_OF_WEEK, DEFAULT_SCHEDULE, TIMEZONES, EMPTY_OFFICE_HOURS } from "../constants";
import { Clock as ClockIconIx } from "iconsax-react";
import PillSwitch, { pillSwitchLabelStartSx } from "./PillSwitch";
import { LabeledField, inboxInputSx, inboxSelectSx, inboxSelectMenuProps, inboxMenuItemSx } from "./LabeledField";

export default function OfficeHoursSection({ officeHours, setForm }) {
  const oh = officeHours || EMPTY_OFFICE_HOURS;

  const schedule = DAYS_OF_WEEK.map(({ day }) => {
    const saved = (oh.schedule || []).find((s) => s.day === day);
    return saved || DEFAULT_SCHEDULE.find((d) => d.day === day);
  });

  const setOH = (patch) =>
    setForm((prev) => ({
      ...prev,
      officeHours: { ...(prev.officeHours || EMPTY_OFFICE_HOURS), ...patch },
    }));

  const updateDay = (day, patch) =>
    setOH({ schedule: schedule.map((s) => (s.day === day ? { ...s, ...patch } : s)) });

  return (
    <Box>
      {/* Icon stays a flex-start sibling of ONE column (tight gap:0.5,
          same as SectionCard/Fallback Message — icon height never touches
          this gap since it's outside the column entirely). The toggle
          lives INSIDE that column, next to the description specifically,
          not as a sibling of the whole icon+heading+description block
          (Rahim, 2026-09-03: previous two-row split re-inflated the
          heading-to-description gap back to the exact bug already fixed
          once — "why did you make so much space ... should be the same as
          other component"). This is the one structure that satisfies both
          asks: tight heading/description spacing AND the toggle aligned
          to the description's own line. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mt: 0.5 }}>
        <Box
          sx={{
            width: 32, height: 32, flexShrink: 0,
            borderRadius: "8px",
            border: "1px solid", borderColor: "divider",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "text.primary",
          }}
        >
          <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ClockIconIx size="100%" color="currentColor" variant="Linear" />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>Office Hours</Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.25 }}>
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
              Restrict the agent to reply only during set hours.
            </Typography>
            <FormControlLabel
              control={
                <PillSwitch
                  checked={!!oh.enabled}
                  onChange={(e) => setOH({ enabled: e.target.checked })}
                />
              }
              label={oh.enabled ? "Enabled" : "Disabled"}
              labelPlacement="start"
              sx={pillSwitchLabelStartSx}
            />
          </Box>
        </Box>
      </Box>

      <Collapse in={!!oh.enabled} unmountOnExit>
        {/* This whole block was still the raw pre-redesign MUI defaults —
            floating InputLabel (tiny, cramped against the box border) on
            Timezone, plain-default TextFields for the time inputs, a
            body2 day label with no explicit size — the one part of this
            section that never got the LabeledField/inboxInputSx/
            inboxSelectSx pass every other field on this page already has
            (Rahim, 2026-09-03: "fix the text font size and content
            spacing"). Bumped gap 1.5->2 to match the page's own
            section-internal rhythm too. */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1.5 }}>
          <LabeledField label="Timezone" sx={{ width: 280 }}>
            <Select
              fullWidth size="small"
              value={oh.timezone || "UTC"}
              onChange={(e) => setOH({ timezone: e.target.value })}
              MenuProps={inboxSelectMenuProps}
              sx={inboxSelectSx}
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz} sx={inboxMenuItemSx}>{tz}</MenuItem>
              ))}
            </Select>
          </LabeledField>

          {/* Row gap 0.75->1.25 — rows read cramped stacked that tight
              once the day label/time fields below got their own real
              font sizes instead of borrowing whatever body2 defaulted
              to. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {schedule.map(({ day, active, from, to }) => {
              const { label } = DAYS_OF_WEEK.find((d) => d.day === day);
              return (
                <Box key={day} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography
                    sx={{
                      width: 96, fontSize: "0.875rem", fontWeight: 500, userSelect: "none",
                      color: active ? "text.primary" : "text.disabled",
                    }}
                  >
                    {label}
                  </Typography>
                  <PillSwitch
                    checked={!!active}
                    onChange={(e) => updateDay(day, { active: e.target.checked })}
                  />
                  {/* 120->140 (Rahim, 2026-09-03: "increase the width of
                      the time cause pm and am is being cut off") —
                      inboxInputSx's border/radius ate into the native
                      time input's already-tight rendering of "09:00 AM",
                      clipping the M. */}
                  <TextField
                    type="time" size="small" value={from || "09:00"}
                    onChange={(e) => updateDay(day, { from: e.target.value })}
                    disabled={!active} inputProps={{ step: 300 }} sx={{ width: 140, ...inboxInputSx }}
                  />
                  <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>to</Typography>
                  <TextField
                    type="time" size="small" value={to || "17:00"}
                    onChange={(e) => updateDay(day, { to: e.target.value })}
                    disabled={!active} inputProps={{ step: 300 }} sx={{ width: 140, ...inboxInputSx }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
